<template>
  <div v-if="shouldShowBanner" class="pwa-install-wrap">
    <div class="pwa-install-banner">
      <img
        class="pwa-install-logo"
        src="@/assets/icons/OhYesMainLogo.png"
        alt="Oh Yes"
      />
      <div class="pwa-install-copy">
        <strong>Install Oh Yes</strong>
        <span>Quick access from your home screen</span>
      </div>
      <v-btn
        small
        depressed
        class="pwa-install-btn"
        @click="handleInstall"
      >
        Install
      </v-btn>
      <v-btn
        icon
        small
        class="pwa-install-close"
        aria-label="Dismiss install prompt"
        @click="dismissBanner"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>

    <v-bottom-sheet v-model="showIosGuide" max-width="420">
      <v-card class="ios-guide-card">
        <v-card-title class="ios-guide-title">
          <img
            class="ios-guide-logo"
            src="@/assets/icons/OhYesMainLogo.png"
            alt="Oh Yes"
          />
          <v-btn icon small @click="showIosGuide = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="ios-guide-steps">
          <div class="ios-guide-step">
            <v-icon color="#1697f6">mdi-export-variant</v-icon>
            <span>Tap Share in Safari</span>
          </div>
          <div class="ios-guide-step">
            <v-icon color="#1697f6">mdi-plus-box-outline</v-icon>
            <span>Choose Add to Home Screen</span>
          </div>
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
const DISMISS_KEY = "ohyesPwaInstallDismissedAt";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export default {
  name: "PwaInstallBanner",
  data() {
    return {
      deferredInstallPrompt: null,
      isMobileViewport: false,
      isAppInstalled: false,
      showBanner: false,
      showIosGuide: false,
      promptTimer: null,
    };
  },
  computed: {
    isIos() {
      const userAgent = window.navigator.userAgent;
      const isTouchMac =
        window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1;

      return /iphone|ipad|ipod/i.test(userAgent) || isTouchMac;
    },
    canShowInstallAction() {
      return this.isIos || !!this.deferredInstallPrompt;
    },
    shouldShowBanner() {
      return (
        this.showBanner &&
        this.isMobileViewport &&
        !this.isAppInstalled &&
        this.canShowInstallAction
      );
    },
  },
  mounted() {
    this.updateDeviceState();
    window.addEventListener("resize", this.updateDeviceState);
    window.addEventListener("beforeinstallprompt", this.captureInstallPrompt);
    window.addEventListener("appinstalled", this.markInstalled);

    if (this.isIos) {
      this.scheduleBanner();
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDeviceState);
    window.removeEventListener(
      "beforeinstallprompt",
      this.captureInstallPrompt
    );
    window.removeEventListener("appinstalled", this.markInstalled);
    window.clearTimeout(this.promptTimer);
  },
  methods: {
    updateDeviceState() {
      this.isMobileViewport = window.matchMedia("(max-width: 767px)").matches;
      this.isAppInstalled =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
    },
    captureInstallPrompt(event) {
      event.preventDefault();
      this.deferredInstallPrompt = event;
      this.scheduleBanner();
    },
    scheduleBanner() {
      if (this.wasRecentlyDismissed() || this.isAppInstalled) {
        return;
      }

      window.clearTimeout(this.promptTimer);
      this.promptTimer = window.setTimeout(() => {
        this.showBanner = true;
      }, 1200);
    },
    async handleInstall() {
      if (this.isIos) {
        this.showIosGuide = true;
        return;
      }

      if (!this.deferredInstallPrompt) {
        return;
      }

      this.deferredInstallPrompt.prompt();
      await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      this.dismissBanner();
    },
    dismissBanner() {
      this.showBanner = false;
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    },
    markInstalled() {
      this.isAppInstalled = true;
      this.showBanner = false;
      this.deferredInstallPrompt = null;
    },
    wasRecentlyDismissed() {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      return dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION_MS;
    },
  },
};
</script>

<style scoped>
.pwa-install-wrap {
  position: fixed;
  right: 12px;
  bottom: 86px;
  left: 12px;
  z-index: 10001;
  pointer-events: none;
}

.pwa-install-banner {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  max-width: 420px;
  margin: 0 auto;
  padding: 10px;
  border: 1px solid rgba(22, 151, 246, 0.2);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  pointer-events: auto;
}

.pwa-install-logo {
  width: 54px;
  height: 54px;
  object-fit: contain;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  padding: 5px;
  background: #ffffff;
}

.pwa-install-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  color: #0f172a;
  line-height: 1.2;
}

.pwa-install-copy strong {
  font-size: 14px;
  font-weight: 700;
}

.pwa-install-copy span {
  margin-top: 3px;
  color: #475569;
  font-size: 12px;
}

.pwa-install-btn {
  min-width: 70px !important;
  border-radius: 6px;
  background: #1697f6 !important;
  color: #ffffff !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.pwa-install-close {
  color: #64748b !important;
}

.ios-guide-card {
  border-radius: 8px 8px 0 0;
}

.ios-guide-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
}

.ios-guide-logo {
  width: 116px;
  height: 42px;
  object-fit: contain;
}

.ios-guide-steps {
  display: grid;
  gap: 12px;
  padding: 12px 18px 22px !important;
}

.ios-guide-step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
}

@media (max-width: 360px) {
  .pwa-install-banner {
    grid-template-columns: 46px minmax(0, 1fr) auto;
  }

  .pwa-install-logo {
    width: 46px;
    height: 46px;
  }

  .pwa-install-close {
    display: none;
  }
}
</style>
